import { useParams } from 'react-router-dom'
import useVehicle from '@/hooks/useVehicle'
import { useVehicleChargeSchedule } from '@/hooks/useVehicleChargeSchedule'
import { useTripsForVehicle } from '@/reducers/trips'
import { vehicleActiveChargeSettingsCollection } from '@/reducers/vehicleActiveChargeSettings'
import { generateLimitsText } from '@/types/chargeSchedule/chargeSchedule.utils'
import {
  daysOfWeekToScheduleDescription,
  scheduleToGoalsDescriptions,
} from '@/app/pages/utility-program/unenroll/suggestions/components/utils/chargeScheduleUtils'
import { unenrollmentVehicleDisplayName } from '@/app/pages/utility-program/unenroll/utils'
import { VehicleActiveChargeSettings } from '@/types/activeChargeSettings'
import { VehicleTrip } from '@/reducers/trips'
import { ChargeSchedule } from '@/types/chargeSchedule'

export type PrescribedGoal = {
  label: string
  requiredDirection: 'on' | 'off'
}

// Solar Surplus is intentionally omitted: it isn't surfaced in the
// configure-devices UI today, so we shouldn't render alerts for it.
const PRESCRIBED_GOAL_LABELS: Array<{
  key: keyof Pick<
    VehicleActiveChargeSettings,
    'money_saving_plan_enabled' | 'save_the_planet_plan_enabled'
  >
  label: string
}> = [
  { key: 'money_saving_plan_enabled', label: 'Save Money' },
  { key: 'save_the_planet_plan_enabled', label: 'Save the Planet' },
]

export function getPrescribedGoals(
  settings: VehicleActiveChargeSettings | undefined,
): PrescribedGoal[] {
  if (!settings) {
    return []
  }
  return PRESCRIBED_GOAL_LABELS.filter(
    ({ key }) => settings[key].is_program_prescribed,
  ).map(({ key, label }) => ({
    label,
    requiredDirection: settings[key].value ? 'on' : 'off',
  }))
}

function activeSettingsToGoalsAdapter(
  settings: VehicleActiveChargeSettings | undefined,
): Pick<
  ChargeSchedule,
  | 'money_saving_plan_enabled'
  | 'save_the_planet_plan_enabled'
  | 'solar_surplus_plan_enabled'
> {
  return {
    money_saving_plan_enabled:
      settings?.money_saving_plan_enabled.value ?? false,
    save_the_planet_plan_enabled:
      settings?.save_the_planet_plan_enabled.value ?? false,
    solar_surplus_plan_enabled:
      settings?.solar_surplus_plan_enabled.value ?? false,
  }
}

// Hidden in configure-devices UI — see PRESCRIBED_GOAL_LABELS above.
const HIDDEN_GOAL_LABELS = new Set(['Solar Surplus'])

export function getGoalsValueText(
  settings: VehicleActiveChargeSettings | undefined,
): string {
  const adapter = activeSettingsToGoalsAdapter(settings)
  const { enabledPlans, disabledPlans } = scheduleToGoalsDescriptions(
    adapter as ChargeSchedule,
  )
  return [
    ...enabledPlans
      .filter((p) => !HIDDEN_GOAL_LABELS.has(p))
      .map((p) => `${p}: Enabled`),
    ...disabledPlans
      .filter((p) => !HIDDEN_GOAL_LABELS.has(p))
      .map((p) => `${p}: Disabled`),
  ].join(', ')
}

export function getNextTripText(trips: VehicleTrip[]): string {
  const now = new Date()
  const upcoming = trips.filter(
    (trip) => new Date(trip.departure_datetime_utc) > now,
  )
  if (upcoming.length === 0) {
    return 'No upcoming trips planned'
  }
  const next = upcoming[0]
  const dateStr = new Date(next.departure_datetime_utc).toLocaleDateString(
    undefined,
    { month: 'short', day: 'numeric' },
  )
  return `Next: ${next.battery_target_max}% by ${dateStr}`
}

export default function useConfigureVehicleViewModel() {
  const { deviceId } = useParams<{ deviceId: string }>()
  const vehicleId = Number(deviceId)
  const vehicle = useVehicle(vehicleId)
  const chargeSchedule = useVehicleChargeSchedule(vehicleId)
  const { data: settingsArray, isLoadingOrIdle: settingsLoading } =
    vehicleActiveChargeSettingsCollection.useFetch(deviceId)
  const settings = settingsArray[0] as VehicleActiveChargeSettings | undefined
  const { trips, status: tripsStatus } = useTripsForVehicle(vehicleId)

  const isLoadingOrIdle =
    !vehicle ||
    !chargeSchedule ||
    settingsLoading ||
    tripsStatus === 'idle' ||
    tripsStatus === 'loading'

  const prescribedGoals = getPrescribedGoals(settings)
  const goalsArePrescribed = prescribedGoals.length > 0

  const limitsText = generateLimitsText(chargeSchedule)
  const goalsValueText = getGoalsValueText(settings)
  const scheduleDescriptions = daysOfWeekToScheduleDescription(
    chargeSchedule?.charge_schedule_days_of_week ?? [],
  )
  const tripsValueText = getNextTripText(trips)
  const displayName = vehicle ? unenrollmentVehicleDisplayName(vehicle) : ''

  const settingPaths = {
    limits: `/devices/vehicle/${vehicleId}/limits`,
    goals: `/devices/vehicle/${vehicleId}/goals`,
    scheduling: `/devices/vehicle/${vehicleId}/scheduling`,
    trips: `/devices/vehicle/${vehicleId}/trips`,
  }

  return {
    isLoadingOrIdle,
    vehicle,
    displayName,
    limitsText,
    goalsValueText,
    scheduleDescriptions,
    tripsValueText,
    goalsArePrescribed,
    prescribedGoals,
    settingPaths,
  }
}
